import React from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/react-hooks";
import MyTaxApplicationWrapper from "my-tax/components/application-wrapper/application-wrapper";
import CombinedTaxCreditApplication from "my-tax/components/combined-tax-credit-application";
import FamilyTaxCreditApplication from "my-tax/components/family-tax-credit-application";
import FirstMarriageTaxCreditApplication from "my-tax/components/first-marriage-tax-credit-application";
import FourChildrenTaxCreditApplication from "my-tax/components/four-children-tax-credit-application";
import MyPersonalTaxCreditApplication from "my-tax/components/my-personal-tax-credit-application";
import UnderThirtyMothersTaxCreditApplication from "my-tax/components/under-thirty-mothers-tax-credit-application";
import UnderTwentyFiveTaxCreditApplication from "my-tax/components/under-twenty-five-tax-credit-application";
import MyTaxTwoOrMoreChildrenTaxCreditApplication from "my-tax/components/two-or-more-children-tax-credit-application";
import { MY_TAX_REMOVE_DECLARATION_REQUEST } from "my-tax/graphql";
import useTaxApplications from "my-tax/hooks/use-tax-applications";
import { useOcSnackbar } from "oc/hooks";
import { useOcIsMobile } from "oc/hooks/use-is-mobile";
import { getFullname } from "oc/utils";

interface MyTaxCreditApplicationsProps {
  label: string;
  employee: any; // Replace 'any' with the appropriate type if available
  taxApplicationSaveEnabled: boolean;
  requestState: any; // Replace 'any' with the appropriate type if available
  selected: any; // Replace 'any' with the appropriate type if available
  setSelected: (value: any) => void; // Replace 'any' with the appropriate type if available
  resetSelectedState: () => void;
}

export const MyTaxCreditApplications: React.FC<
  MyTaxCreditApplicationsProps
> = ({
  label,
  employee,
  taxApplicationSaveEnabled,
  selected,
  setSelected,
  resetSelectedState,
}) => {
    const { t } = useTranslation();
    const fullName = getFullname(employee) || "";
    const taxNumber = employee?.taxNumber || "";
    const isMobile = useOcIsMobile();
    const { setTaxApplications } = useTaxApplications();

    const { setOpenMessage } = useOcSnackbar();
    const [removeDeclaration] = useMutation(MY_TAX_REMOVE_DECLARATION_REQUEST, {
      onCompleted: (data) => {
        const res = data?.removeTaxDeclarationRequest;
        if (res?.result) {
          if (res?.messages?.length > 0) {
            const message = res?.messages[0];
            if (message === "A korábban beadott nyilatkozata törlésre került!") {
              setOpenMessage({
                type: "success",
                message: t("MY_TAX_DECLARATION_REMOVED_SUCCESSFULLY"),
              });
              setOpenMessage({
                type: "warning",
                message: t("MY_TAX_PREVIOUS_TAX_DECLARATION_DELETED"),
              });
            }
          } else {
            setOpenMessage({
              type: "success",
              message: t("MY_TAX_DECLARATION_REMOVED_SUCCESSFULLY"),
            });
          }
          resetSelectedState();
        } else {
          setOpenMessage({
            type: "error",
            message: res?.messages || t("MY_TAX_DECLARATION_REMOVED_FAILED"),
          });
        }
      },
      onError: () => {
        setOpenMessage({
          type: "error",
          message: t("MY_TAX_DECLARATION_REMOVED_FAILED"),
        });
      },
    });

    const handleRemoveRequest = () => {
      const params = {
        year: selected?.year,
        taxDeclarationType: selected?.type,
      };
      removeDeclaration({ variables: params });
      setTimeout(() => {
        setTaxApplications((prev: Record<string, boolean>) => {
          const updated = { ...prev, [selected?.type]: true };
          return updated;
        });
      }, 800);
    };

    switch (selected?.type) {
      case "TaxDeclarationCombined":
        return (
          <CombinedTaxCreditApplication
            fullName={fullName}
            taxNumber={taxNumber}
            taxApplicationSaveEnabled={taxApplicationSaveEnabled}
            handleRemoveRequest={handleRemoveRequest}
            selected={selected}
            setSelected={setSelected}
            isMobile={isMobile}
          />
        );
      case "TaxDeclarationPersonal":
        return (
          <MyPersonalTaxCreditApplication
            fullName={fullName}
            taxNumber={taxNumber}
            taxApplicationSaveEnabled={taxApplicationSaveEnabled}
            handleRemoveRequest={handleRemoveRequest}
            selected={selected}
            setSelected={setSelected}
            isMobile={isMobile}
          />
        );
      case "TaxDeclarationFirstMarriedPerson":
        return (
          <FirstMarriageTaxCreditApplication
            fullName={fullName}
            taxNumber={taxNumber}
            taxApplicationSaveEnabled={taxApplicationSaveEnabled}
            handleRemoveRequest={handleRemoveRequest}
            selected={selected}
            setSelected={setSelected}
            isMobile={isMobile}
          />
        );
      case "TaxDeclarationFourChildren":
        return (
          <FourChildrenTaxCreditApplication
            fullName={fullName}
            taxNumber={taxNumber}
            taxApplicationSaveEnabled={taxApplicationSaveEnabled}
            handleRemoveRequest={handleRemoveRequest}
            selected={selected}
            setSelected={setSelected}
            isMobile={isMobile}
          />
        );
      case "TaxDeclarationFamily":
        return (
          <FamilyTaxCreditApplication
            fullName={fullName}
            taxNumber={taxNumber}
            taxApplicationSaveEnabled={taxApplicationSaveEnabled}
            handleRemoveRequest={handleRemoveRequest}
            selected={selected}
            setSelected={setSelected}
            isMobile={isMobile}
          />
        );
      case "TaxDeclarationUnder25":
        return (
          <UnderTwentyFiveTaxCreditApplication
            fullName={fullName}
            taxNumber={taxNumber}
            taxApplicationSaveEnabled={taxApplicationSaveEnabled}
            handleRemoveRequest={handleRemoveRequest}
            selected={selected}
            setSelected={setSelected}
            isMobile={isMobile}
          />
        );
      case "TaxDecarationMotherUnder30":
        return (
          <UnderThirtyMothersTaxCreditApplication
            fullName={fullName}
            taxNumber={taxNumber}
            taxApplicationSaveEnabled={taxApplicationSaveEnabled}
            handleRemoveRequest={handleRemoveRequest}
            selected={selected}
            setSelected={setSelected}
            isMobile={isMobile}
          />
        );
      case "TaxDeclarationThreeChildren":
        return (
          <MyTaxTwoOrMoreChildrenTaxCreditApplication
            fullName={fullName}
            taxNumber={taxNumber}
            taxApplicationSaveEnabled={taxApplicationSaveEnabled}
            handleRemoveRequest={handleRemoveRequest}
            selected={selected}
            setSelected={setSelected}
            isMobile={isMobile}
          />
        );
      default:
        return <MyTaxApplicationWrapper label={label} />;
    }
  };

export default MyTaxCreditApplications;
